import React from 'react';
import { useParams } from 'react-router-dom';

import {
    computeBoundaryNeeds,
    computeNeighbors
} from '../../../../components/ListNav/computeNeighbors';
import type { ListNeighborsResult, ListSnapshot, Row } from '../../../../components/ListNav/types';
import {
    getListBackTo,
    useListSnapshot,
    writeListSnapshot
} from '../../../../components/ListNav/useListSnapshot';
import useGetLeadsListQuery from '../../api/useGetLeadsListQuery';
import useSearchOrgIds from '../../../../hooks/useSearchOrgIds';
import { LeadDataResponse, LeadQueryParams } from '../../types';

export type FormFillsListSnapshot = ListSnapshot<LeadQueryParams>;

const SESSION_KEY = 'formFillsListSnapshot';
const FALLBACK_BACK_TO = '/form-fills';
export const FORM_FILLS_LIST_KIND = 'form-fills';

export const writeFormFillsListSnapshot = (snapshot: FormFillsListSnapshot) =>
    writeListSnapshot(SESSION_KEY, snapshot);

export const getFormFillsListBackTo = (locationState: unknown) =>
    getListBackTo(locationState, SESSION_KEY, FALLBACK_BACK_TO);

const formatName = (firstName?: string | null, lastName?: string | null) => {
    const combined = `${firstName ?? ''} ${lastName ?? ''}`.trim();
    return combined.length ? combined : 'N/A';
};

const normalize = (data?: LeadDataResponse): Row[] => {
    if (!data?.leads) return [];
    return data.leads.map((lead) => ({
        name: formatName(lead.first_name, lead.last_name),
        url: `/form-fills/${lead.lead_id}`
    }));
};

const useFormFillsListNeighbors = (): ListNeighborsResult => {
    const { id } = useParams<{ id: string }>();

    const snapshot = useListSnapshot<LeadQueryParams>(SESSION_KEY);
    const listQuery: LeadQueryParams = (snapshot?.listQuery ?? {}) as LeadQueryParams;

    // [HWM-23014] The snapshot replays the list query, so it must be scoped to the same providers
    // the list was searched across. Without this the replay is scoped to the active org alone, and
    // prev/next walk a different row set than the list the user came from (and "N of M" is wrong).
    const searchOrgIds = useSearchOrgIds(listQuery.search);

    const mainQ = useGetLeadsListQuery(listQuery, {
        enabled: !!snapshot,
        heroOrgIds: searchOrgIds
    });

    const rows = React.useMemo(() => normalize(mainQ.data), [mainQ.data]);

    const limit = Number((listQuery as { limit?: number | string }).limit ?? 10) || 10;
    const offsetNum = Number((listQuery as { offset?: number | string }).offset ?? 0) || 0;
    const totalCount = mainQ.data?.total_count ?? 0;

    const currentKey = id ? `/form-fills/${id}` : '';
    const currentIdx = currentKey ? rows.findIndex((r) => r.url === currentKey) : -1;

    const { needPrevPage, needNextPage } = computeBoundaryNeeds(
        currentIdx,
        rows,
        offsetNum,
        limit,
        totalCount
    );

    const prevPageQuery = {
        ...listQuery,
        offset: String(offsetNum - limit)
    } as unknown as LeadQueryParams;
    const nextPageQuery = {
        ...listQuery,
        offset: String(offsetNum + limit)
    } as unknown as LeadQueryParams;

    const prevPageQ = useGetLeadsListQuery(prevPageQuery, {
        enabled: !!snapshot && needPrevPage,
        heroOrgIds: searchOrgIds
    });
    const nextPageQ = useGetLeadsListQuery(nextPageQuery, {
        enabled: !!snapshot && needNextPage,
        heroOrgIds: searchOrgIds
    });

    const prevPageRows = React.useMemo(() => normalize(prevPageQ.data), [prevPageQ.data]);
    const nextPageRows = React.useMemo(() => normalize(nextPageQ.data), [nextPageQ.data]);

    const { prev, next, position } = computeNeighbors({
        rows,
        currentIdx,
        offset: offsetNum,
        limit,
        totalCount,
        snapshot,
        prevPageRows,
        nextPageRows
    });

    return {
        prev,
        next,
        position,
        backTo: snapshot?.from ?? FALLBACK_BACK_TO
    };
};

export default useFormFillsListNeighbors;
